import React, { useState } from "react";

import { CheckCircle2, Loader, Send } from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../utils/apiClient";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";

interface LeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues?: Partial<{
    name: string;
    company: string;
    website: string;
    email: string;
    message: string;
  }>;
}

const EMPTY_FORM = { name: "", company: "", website: "", email: "", message: "" };

const LeadModal = ({ open, onOpenChange, defaultValues }: LeadModalProps) => {
  const { user, businessProfile } = useAuth();
  const [form, setForm] = useState({ ...EMPTY_FORM, ...defaultValues });

  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      const base = { ...EMPTY_FORM, ...defaultValues };
      setForm(
        !user
          ? base
          : {
              ...base,
              name: user.name || base.name,
              email: user.email || base.email,
              company: businessProfile?.business_name || base.company,
              website: businessProfile?.business_domain || base.website,
            },
      );
    }
  }

  const [status, setStatus] = useState<{
    loading: boolean;
    success: boolean;
    error: string | null;
  }>({
    loading: false,
    success: false,
    error: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    try {
      const details = [
        form.company && `Company: ${form.company}`,
        form.website && `Website: ${form.website}`,
        form.message,
      ]
        .filter(Boolean)
        .join("\n\n");

      await api.post(
        "/inquiry",
        {
          name: form.name,
          email: form.email,
          details,
        },
        true,
      );

      setStatus({ loading: false, success: true, error: null });
      setForm({ name: "", company: "", website: "", email: "", message: "" });

      setTimeout(() => {
        onOpenChange(false);
        setStatus({ loading: false, success: false, error: null });
      }, 2000);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      const errorMessage =
        err.response?.data?.detail || "Failed to send inquiry. Please try again.";
      setStatus({ loading: false, success: false, error: errorMessage });
    }
  };

  const inputClasses =
    "w-full bg-surface-container-low border-none rounded-xl px-5 py-3.5 text-on-surface text-sm placeholder:text-outline/50 focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition-all outline-none";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Talk to an Expert</DialogTitle>
          <DialogDescription>
            Get a custom strategy session with our AI search specialists.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Acme Hotels"
                  className={inputClasses}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                Website
              </label>
              <input
                type="url"
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="https://your-hotel.com"
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                Email <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="john@acmehotels.com"
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                Message <span className="text-error">*</span>
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                placeholder="How can we help your hotel thrive in AI search?"
                rows={3}
                className={`${inputClasses} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={status.loading || status.success}
              className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-3.5 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2.5 disabled:opacity-70 mt-2"
            >
              {status.loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" /> Sending...
                </>
              ) : status.success ? (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Sent Successfully
                </>
              ) : (
                <>
                  Request Consultation <Send className="w-4 h-4" />
                </>
              )}
            </button>

            {status.error && (
              <p className="text-error text-center text-sm font-medium">{status.error}</p>
            )}
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default LeadModal;
